import React, { useState, useRef, useEffect } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import { buildDownloadFilename } from '@/utils/downloadFilename';

interface DownloadButtonProps {
  imageUrl: string;
  filename: string;
  size?: 'sm' | 'md';
  className?: string;
}

const isDataUrl = (url: string): boolean => url.startsWith('data:');

const formatBytes = (sizeInBytes: number): string => {
  const sizeInMB = sizeInBytes / (1024 * 1024);
  return sizeInMB >= 1 ? `${sizeInMB.toFixed(1)} MB` : `${(sizeInBytes / 1024).toFixed(0)} KB`;
};

const mimeTypeForFormat = (format: 'jpg' | 'png'): string => (format === 'png' ? 'image/png' : 'image/jpeg');

const blobFromDataUrl = (dataUrl: string): Blob => {
  const [header, payload] = dataUrl.split(',');
  if (!header || payload === undefined) {
    throw new Error('Invalid data URL');
  }

  const mimeMatch = header.match(/data:([^;]+)/);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';
  const isBase64 = header.includes(';base64');

  if (isBase64) {
    const binaryString = window.atob(payload);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  return new Blob([decodeURIComponent(payload)], { type: mimeType });
};

const convertBlobFormat = async (sourceBlob: Blob, targetFormat: 'jpg' | 'png'): Promise<Blob> => {
  const targetMime = mimeTypeForFormat(targetFormat);
  if (sourceBlob.type === targetMime) {
    return sourceBlob;
  }

  const objectUrl = window.URL.createObjectURL(sourceBlob);
  try {
    const imageElement = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for conversion'));
      img.src = objectUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = imageElement.naturalWidth || imageElement.width;
    canvas.height = imageElement.naturalHeight || imageElement.height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas context is unavailable');
    }

    context.drawImage(imageElement, 0, 0);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to convert image'));
            return;
          }
          resolve(blob);
        },
        targetMime,
        targetFormat === 'jpg' ? 1 : undefined
      );
    });
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
};

export default function DownloadButton({ imageUrl, filename, size = 'md', className = '' }: DownloadButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fileSizes, setFileSizes] = useState<{ jpg: string; png: string }>({ jpg: '...', png: '...' });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch file sizes when dropdown opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchFileSizes = async () => {
      try {
        const formats = ['jpg', 'png'] as const;
        const sizes: { jpg: string; png: string } = { jpg: '...', png: '...' };

        await Promise.all(
          formats.map(async (format) => {
            try {
              if (isDataUrl(imageUrl)) {
                const sourceBlob = blobFromDataUrl(imageUrl);
                const outputBlob = await convertBlobFormat(sourceBlob, format);
                sizes[format] = formatBytes(outputBlob.size);
                return;
              }

              let url = imageUrl;
              if (imageUrl.includes('/assets/')) {
                const baseUrl = imageUrl.replace(/\.(jpg|jpeg|png)$/i, '');
                url = `${baseUrl}?format=${format}&quality=100`;
              }

              const response = await fetch(url, { method: 'HEAD' });
              const contentLength = response.headers.get('content-length');
              
              if (contentLength) {
                const sizeInBytes = parseInt(contentLength);
                sizes[format] = formatBytes(sizeInBytes);
              }
            } catch (error) {
              console.error(`Error fetching size for ${format}:`, error);
            }
          })
        );

        setFileSizes(sizes);
      } catch (error) {
        console.error('Error fetching file sizes:', error);
      }
    };

    fetchFileSizes();
  }, [isOpen, imageUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleDownload = async (format: 'jpg' | 'png') => {
    setDownloading(true);
    setIsOpen(false);
    
    try {
      let blob: Blob;

      if (isDataUrl(imageUrl)) {
        const sourceBlob = blobFromDataUrl(imageUrl);
        blob = await convertBlobFormat(sourceBlob, format);
      } else {
        // Build Directus URL with format transformation
        let downloadUrl = imageUrl;
        
        // If it's a Directus URL (contains /assets/), add format parameter
        if (imageUrl.includes('/assets/')) {
          const baseUrl = imageUrl.replace(/\.(jpg|jpeg|png)$/i, '');
          downloadUrl = `${baseUrl}?format=${format}&quality=100&download`;
        }
        
        // Fetch the image as blob
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error('Download failed');
        
        blob = await response.blob();
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      link.download = buildDownloadFilename(filename, format);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const buttonSizeClasses = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const iconSizeClasses = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleFormatClick = (e: React.MouseEvent, format: 'jpg' | 'png') => {
    e.preventDefault();
    e.stopPropagation();
    handleDownload(format);
  };

  const spinnerSizeClasses = size === 'sm' ? 'w-3.5 h-3.5 border-2' : 'w-4 h-4 border-2';

  return (
    <div className="relative pointer-events-auto" ref={dropdownRef}>
      <button
        onClick={handleButtonClick}
        disabled={downloading}
        className={`peer ${buttonSizeClasses} bg-black/70 backdrop-blur-md border border-white/40 rounded-lg text-white hover:bg-black/90 hover:border-white/60 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg ${className}`}
        title={t('button.download')}
      >
        {downloading ? (
          <div className={`${spinnerSizeClasses} border-white border-t-transparent rounded-full animate-spin`}></div>
        ) : (
          <Download className={iconSizeClasses} strokeWidth={2} />
        )}
      </button>
      {!isOpen && (
        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/20 bg-black/80 px-2 py-1 text-[10px] text-white opacity-0 transition-opacity duration-150 peer-hover:opacity-100">
          {t('tooltip.download')}
        </span>
      )}

      {/* Dropdown Menu */}
      {isOpen && !downloading && (
        <div 
          className="absolute top-full right-0 mt-2 w-40 bg-bg-panel border border-border rounded-lg shadow-2xl overflow-visible z-[9999] pointer-events-auto animate-in fade-in slide-in-from-top-1 duration-150"
        >
          <button
            onClick={(e) => handleFormatClick(e, 'jpg')}
            className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-bg-control transition-colors flex items-center justify-between group"
          >
            <span className="font-medium">JPG</span>
            <span className="text-xs text-text-secondary group-hover:text-text-primary font-mono">
              {fileSizes.jpg}
            </span>
          </button>
          <div className="h-px bg-border"></div>
          <button
            onClick={(e) => handleFormatClick(e, 'png')}
            className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-bg-control transition-colors flex items-center justify-between group"
          >
            <span className="font-medium">PNG</span>
            <span className="text-xs text-text-secondary group-hover:text-text-primary font-mono">
              {fileSizes.png}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
